import { OutreachStatus } from "@plenum/shared/api";
import { getRouteApi, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ApiError,
  useCreateOutreach,
  useExport,
  useList,
  useOutreachBoard,
  useRemoveFromList,
  useRenameList,
} from "../lib/api";
import { runExport } from "../lib/download";
import { fmtInt, fmtMw } from "../lib/format";
import { usePersisted } from "../lib/theme";
import { ColumnChooser, DataTable } from "../ui/DataTable";
import { DEFAULT_SITE_COLUMNS, SITE_COLUMNS } from "../ui/siteColumns";
import { useToast } from "../ui/Toast";

const route = getRouteApi("/lists/$id");

export function ListDetailPage() {
  const { id } = route.useParams();
  const listId = Number(id);
  const list = useList(Number.isInteger(listId) ? listId : undefined);
  const remove = useRemoveFromList();
  const rename = useRenameList();
  const exp = useExport();
  const toast = useToast();
  const [visible, setVisible] = usePersisted<string[]>("list:columns", DEFAULT_SITE_COLUMNS);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [columns, setColumns] = useState<"gary" | "engineering" | "full">("gary");
  const board = useOutreachBoard(Number.isInteger(listId) ? listId : undefined);
  const move = useCreateOutreach();
  const [view, setView] = useState<"table" | "board">("table");

  if (list.isLoading)
    return (
      <div className="p-6" style={{ color: "var(--muted)" }}>
        Loading…
      </div>
    );
  if (list.error || !list.data) {
    return (
      <div className="p-6">
        <p style={{ color: "var(--danger)" }}>
          {list.error instanceof ApiError ? list.error.message : "List not found"}
        </p>
        <Link to="/lists">Back to lists</Link>
      </div>
    );
  }
  const l = list.data;
  const mw = l.items.reduce((a, s) => a + s.mw_f, 0);
  const units = l.items.reduce((a, s) => a + s.units_f, 0);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        className="flex flex-wrap items-center gap-3 border-b px-4 py-3"
        style={{ borderColor: "var(--line)", background: "var(--panel)" }}
      >
        <Link to="/lists" className="text-[12px]">
          ← Lists
        </Link>
        {nameDraft === null ? (
          <h1 className="text-[16px] font-semibold" data-testid="list-name">
            {l.name}{" "}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setNameDraft(l.name)}
              aria-label="Rename list"
            >
              rename
            </button>
          </h1>
        ) : (
          <form
            className="flex items-center gap-1"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await rename.mutateAsync({ listId, name: nameDraft.trim() || l.name });
                setNameDraft(null);
              } catch (err) {
                toast(err instanceof ApiError ? err.message : "Rename failed", "danger");
              }
            }}
          >
            <input
              className="input"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              aria-label="List name"
            />
            <button type="submit" className="btn btn-primary btn-sm">
              Save
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setNameDraft(null)}>
              Cancel
            </button>
          </form>
        )}
        <span
          className="num text-[12px]"
          style={{ color: "var(--muted)" }}
          data-testid="list-summary"
        >
          {fmtInt(l.item_count)} sites · {fmtInt(units)} units · {fmtMw(mw)} MW
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/" search={{ list: listId }} className="btn btn-sm">
            Show on map
          </Link>
          <select
            className="input"
            value={columns}
            onChange={(e) => setColumns(e.target.value as typeof columns)}
            aria-label="Export column set"
          >
            <option value="gary">Baseline list</option>
            <option value="engineering">Engineering</option>
            <option value="full">Full (per unit)</option>
          </select>
          <button
            type="button"
            className="btn btn-sm"
            disabled={exp.isPending || l.item_count === 0}
            onClick={() =>
              runExport(exp.mutateAsync, { list_id: listId, format: "csv", columns }, toast)
            }
            data-testid="export-csv"
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn btn-sm"
            disabled={exp.isPending || l.item_count === 0}
            onClick={() =>
              runExport(exp.mutateAsync, { list_id: listId, format: "geojson", columns }, toast)
            }
            data-testid="export-geojson"
          >
            GeoJSON
          </button>
          <ColumnChooser columns={SITE_COLUMNS} visible={visible} onChange={setVisible} />
        </div>
      </div>
      <div
        className="flex items-center gap-2 border-b px-4 py-1.5 text-[12px]"
        style={{ borderColor: "var(--line)" }}
      >
        <button
          type="button"
          className="chip"
          aria-pressed={view === "table"}
          onClick={() => setView("table")}
        >
          Table
        </button>
        <button
          type="button"
          className="chip"
          aria-pressed={view === "board"}
          onClick={() => setView("board")}
          data-testid="view-board"
        >
          Outreach board
        </button>
        {board.data ? (
          <span className="ml-2" style={{ color: "var(--muted)" }}>
            {OutreachStatus.options
              .map((s) => `${s} ${board.data?.columns[s]?.length ?? 0}`)
              .join(" · ")}
          </span>
        ) : null}
      </div>
      {view === "board" ? (
        <div
          className="scroll-thin flex min-h-0 flex-1 gap-2 overflow-x-auto p-3"
          data-testid="outreach-board"
        >
          {OutreachStatus.options.map((stage) => (
            <div
              key={stage}
              className="flex w-56 shrink-0 flex-col rounded border"
              style={{ borderColor: "var(--line)", background: "var(--panel)" }}
            >
              <div
                className="flex items-center justify-between border-b px-2 py-1"
                style={{ borderColor: "var(--line)" }}
              >
                <span className="eyebrow">{stage}</span>
                <span className="num text-[11px]" style={{ color: "var(--muted-2)" }}>
                  {board.data?.columns[stage]?.length ?? 0}
                </span>
              </div>
              <ul className="scroll-thin flex min-h-[80px] flex-1 flex-col gap-1 overflow-auto p-1">
                {(board.data?.columns[stage] ?? []).map((card) => (
                  <li
                    key={card.site_id}
                    className="rounded border p-2 text-[12px]"
                    style={{ borderColor: "var(--line)", background: "var(--panel-2)" }}
                    data-testid={`card-${card.site_id}`}
                  >
                    <Link
                      to="/sites/$id"
                      params={{ id: String(card.site_id) }}
                      search={{ tab: "activity" }}
                      className="font-medium"
                    >
                      {card.site_name}
                    </Link>
                    <div style={{ color: "var(--muted-2)" }}>
                      {card.state}
                      {card.last_event_at ? ` · ${card.last_event_at.slice(0, 10)}` : ""}
                    </div>
                    {card.last_note ? (
                      <div className="mt-1 truncate" style={{ color: "var(--muted)" }}>
                        {card.last_note}
                      </div>
                    ) : null}
                    <select
                      className="input mt-1 w-full"
                      value={stage}
                      aria-label={`Move ${card.site_name}`}
                      onChange={async (e) => {
                        try {
                          await move.mutateAsync({
                            site_id: card.site_id,
                            status: e.target.value as (typeof OutreachStatus.options)[number],
                            note: `moved on the ${l.name} board`,
                          });
                        } catch (err) {
                          toast(err instanceof ApiError ? err.message : "Move failed", "danger");
                        }
                      }}
                    >
                      {OutreachStatus.options.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
      <div
        className="scroll-thin min-h-0 flex-1 overflow-auto"
        style={{ display: view === "board" ? "none" : undefined }}
      >
        <DataTable
          columns={[
            ...SITE_COLUMNS,
            {
              key: "remove",
              label: "",
              render: (s) => (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label={`Remove ${s.name} from list`}
                  onClick={async () => {
                    try {
                      await remove.mutateAsync({ listId, siteId: s.id });
                    } catch (err) {
                      toast(err instanceof ApiError ? err.message : "Remove failed", "danger");
                    }
                  }}
                >
                  remove
                </button>
              ),
            },
          ]}
          visible={[...visible, "remove"]}
          rows={l.items}
          rowKey={(r) => r.id}
          emptyText="This list is empty. Add sites from the map or the table."
          testId="list-items-table"
        />
      </div>
    </div>
  );
}
